// Updated to use enhanced reminders service
import { remindersService as enhancedRemindersService } from "@/app/_module/services/remindersService";
import type {
  CreateReminderRequest as EnhancedCreateReminderRequest,
  UpdateReminderRequest as EnhancedUpdateReminderRequest,
  ReminderFilters as EnhancedReminderFilters,
} from "@/app/_module/services/remindersService";
import type {
  Reminder,
  CreateReminderRequest,
  UpdateReminderRequest,
  RemindersResponse,
} from "@/app/_module/types/reminders";

const toYmd = (d?: string | Date): string | undefined => {
  if (!d) return undefined;
  try {
    let dateObj: Date;
    if (typeof d === "string") {
      // Parse YYYY-MM-DD as local date to avoid UTC conversion
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [year, month, day] = d.split('-').map(Number);
        dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);
      } else {
        dateObj = new Date(d);
      }
    } else {
      dateObj = d;
    }
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return undefined;
  }
};

// Type adapters for backward compatibility
const adaptCreateReminderRequest = (data: CreateReminderRequest): EnhancedCreateReminderRequest => {
  const date = toYmd(data.date) || toYmd((data as unknown as Record<string, unknown>).dueDate as string | Date | undefined) || data.date;
  const remindAt = (data as unknown as Record<string, unknown>).remindAt as string | undefined;
  const reminderTime = (data as unknown as Record<string, unknown>).reminderTime as string | undefined;
  return {
    title: data.title,
    date,
    text: data.text,
    ...(remindAt ? { remindAt } : {}),
    ...(reminderTime ? { reminderTime } : {}),
    ...(data.associations && data.associations.length > 0 ? { associations: data.associations } : {}),
  };
};

const adaptUpdateReminderRequest = (data: UpdateReminderRequest): EnhancedUpdateReminderRequest => {
  const date = toYmd(data.date) || toYmd((data as unknown as Record<string, unknown>).dueDate as string | Date | undefined) || data.date;
  const reminderTime = (data as unknown as Record<string, unknown>).reminderTime as string | undefined;
  return {
    title: data.title,
    date,
    text: data.text,
    ...(reminderTime ? { reminderTime } : {}),
    ...(data.associations && data.associations.length > 0 ? { associations: data.associations } : {}),
  };
};

const adaptFilters = (params?: {
  entityType?: string;
  page?: number;
  limit?: number;
  all?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  orderBy?: string;
}): EnhancedReminderFilters | undefined => {
  if (!params) return undefined;
  
  return {
    entityType: params.entityType,
    page: params.page,
    limit: params.all ? 1000 : params.limit,
    sortBy: params.sortBy || params.orderBy,
    sortOrder: params.sortOrder,
  };
};

export const runtime = "nodejs";

export const remindersService = {
  // Get all reminders with optional filtering and pagination
  getReminders: async (params?: {
    entityType?: string;
    page?: number;
    limit?: number;
    all?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    orderBy?: string;
  }): Promise<RemindersResponse> => {
    try {
      const adaptedFilters = adaptFilters(params);
      const response = await enhancedRemindersService.getReminders(adaptedFilters);
      
      // Adapt response format for backward compatibility
      return {
        meta: response.data.meta,
        items: response.data.items,
      };
    } catch (error) {
      // Enhanced service already handles errors
      throw error;
    }
  },

  // Get a single reminder by ID
  getReminderById: async (reminderId: string): Promise<Reminder> => {
    try {
      return await enhancedRemindersService.getReminderById(reminderId);
    } catch (error) {
      // Enhanced service already handles errors
      throw error;
    }
  },

  // Create a new reminder
  createReminder: async (
    reminderData: CreateReminderRequest
  ): Promise<Reminder> => {
    try {
      const adaptedData = adaptCreateReminderRequest(reminderData);
      return await enhancedRemindersService.createReminder(adaptedData);
    } catch (error) {
      // Enhanced service already handles errors
      throw error;
    }
  },

  // Update an existing reminder
  updateReminder: async (
    reminderId: string,
    reminderData: UpdateReminderRequest
  ): Promise<Reminder> => {
    try {
      const adaptedData = adaptUpdateReminderRequest(reminderData);
      return await enhancedRemindersService.updateReminder(reminderId, adaptedData);
    } catch (error) {
      // Enhanced service already handles errors
      throw error;
    }
  },

  // Update reminder status/priority via dedicated endpoint
  updateReminderStatus: async (
    reminderId: string,
    statusData: { remStat?: Reminder['status']; remPriorStat?: 'urgent' | 'high' | 'medium' | 'low' }
  ): Promise<Reminder> => {
    try {
      // Use the dedicated status endpoint
      return await enhancedRemindersService.updateReminderStatus(reminderId, statusData);
    } catch (error) {
      throw error;
    }
  },

  // Delete a reminder
  deleteReminder: async (
    reminderId: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      await enhancedRemindersService.deleteReminder(reminderId);
      return { success: true, message: "Reminder deleted successfully" };
    } catch (error) {
      // Enhanced service already handles errors
      throw error;
    }
  },
};